import type { Event } from "@bugsnag/js";

import {
  EMAIL_QUERY_KEY,
  MEETING_QUERY_KEY,
  THREAD_AI_CONTEXT_QUERY_KEY,
  THREAD_QUERY_KEY,
} from "@config/query";
import { ERROR_STRINGS } from "@config/messages";

/**
 * Events Bugsnag should drop before sending. Matched at the end of
 * useBugsnagErrorHandler's notify callback, once the metadata below is attached, and in
 * App.tsx's onError gate. Predicates read three fields populated by useBugsnagErrorHandler:
 * errors[0].errorMessage, getMetadata("react-query").callSite, and
 * getMetadata("http").status — plus event.originalError for auto-captured
 * events (unhandled rejections) that bypass useBugsnagErrorHandler entirely.
 *
 * Add entries below; the `name` field is documentation only.
 */

type BugsnagNoiseFilter = {
  name: string;
  matches: (event: Event) => boolean;
};

const errorMessage = (event: Event): string => event.errors?.[0]?.errorMessage ?? "";

const callSite = (event: Event): string => {
  const raw = event.getMetadata("react-query") as { callSite?: unknown } | undefined;
  return typeof raw?.callSite === "string" ? raw.callSite : "";
};

const httpStatus = (event: Event): number | undefined => {
  const raw = event.getMetadata("http") as { status?: unknown } | undefined;
  return typeof raw?.status === "number" ? raw.status : undefined;
};

// Status off the raw thrown error — the only signal available for auto-captured events,
// which never pass through useBugsnagErrorHandler and so carry no http/react-query metadata.
const originalErrorStatus = (event: Event): number | undefined => {
  const response = (event.originalError as { response?: { status?: unknown } } | undefined)
    ?.response;
  return typeof response?.status === "number" ? response.status : undefined;
};

export const isIgnorableBugsnagEvent = (event: Event): boolean =>
  BUGSNAG_NOISE_FILTERS.some((filter) => filter.matches(event));

const BUGSNAG_NOISE_FILTERS: BugsnagNoiseFilter[] = [
  {
    name: "OAuth re-auth UI: user sees a [couldn't log you in] toast",
    matches: (event) => errorMessage(event) === "Authorization page could not be loaded.",
  },
  {
    name: "OAuth: user declined access",
    matches: (event) => errorMessage(event) === "The user did not approve access.",
  },
  {
    name: "get-email 400 (email not synced)",
    matches: (event) => callSite(event).startsWith(EMAIL_QUERY_KEY) && httpStatus(event) === 400,
  },
  {
    name: "get-thread / get-meeting / thread-AI-context 404 (entity deleted server-side)",
    matches: (event) => {
      const cs = callSite(event);
      return (
        (cs.startsWith(THREAD_QUERY_KEY) ||
          cs.startsWith(MEETING_QUERY_KEY) ||
          cs.startsWith(THREAD_AI_CONTEXT_QUERY_KEY)) &&
        httpStatus(event) === 404
      );
    },
  },
  {
    name: "create-company validation failure (e.g. website duplicate)",
    matches: (event) =>
      callSite(event) === ERROR_STRINGS.CREATE_COMPANY && httpStatus(event) === 400,
  },
  {
    // e.g. a get-email query promise rejecting after rapid item switches, surfaced as an
    // unhandled rejection. 400 = expected user-input/not-synced rejection shown in the UI.
    name: "expected 400 from an auto-captured event (no react-query metadata)",
    matches: (event) => originalErrorStatus(event) === 400,
  },
];
